"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, type RefObject } from "react";

/* ============================================================================
   THE SHEET'S BEHAVIOUR — what makes a bottom-anchored `.v2-panel` act like
   an iOS sheet rather than a picture of one.

   1. The page behind it does not move. A modal <dialog> makes the page inert
      but not still: a swipe on the sheet's head (which has nothing to scroll)
      chained straight through to the document. `useScrollLock` pins the root
      scroller while any dialog is open; `scrollbar-gutter` keeps a desktop
      page from shifting sideways when its scrollbar goes away.
   2. The head is a handle. Dragging it moves the sheet with the finger; let go
      past a quarter of its height (or flick) and it leaves, otherwise it
      settles back. Upward it only gives a little, like the real thing.
   3. The body scrolls, and pulling down on it when it is already at its top
      turns into the same drag — so a sheet can be dismissed from anywhere, and
      a list inside it still scrolls normally.

   The gesture is only armed while the panel is actually a sheet — its bottom
   edge on the viewport's — so a centred desktop dialog is never draggable.
   ========================================================================== */

let lockDepth = 0;
type Saved = {
  overflow: string;
  gutter: string;
  body: Pick<CSSStyleDeclaration, "overflow" | "position" | "top" | "insetInline" | "width">;
  scrollY: number;
};
let saved: Saved | null = null;

/**
 * THE PAGE BEHIND HOLDS STILL — ON iOS TOO.
 *
 * `overflow: hidden` on the root is enough everywhere except iOS Safari,
 * which ignores it for touch: a swipe that reached the page (a short sheet,
 * the backdrop, the head) still scrolled the document underneath, and once
 * the page had taken the gesture the sheet's own list did not move. The one
 * reliable lock there is to fix the body in place at its current offset and
 * put the offset back on release — which is what this does on a touch
 * device, alongside the overflow lock.
 */
export function useScrollLock(active) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== active) {
    t0 = () => {
      if (!active) {
        return;
      }
      const root = document.documentElement;
      const body = document.body;
      const touch = window.matchMedia("(pointer: coarse)").matches;
      if (lockDepth === 0) {
        saved = {
          overflow: root.style.overflow,
          gutter: root.style.scrollbarGutter,
          body: {
            overflow: body.style.overflow,
            position: body.style.position,
            top: body.style.top,
            insetInline: body.style.insetInline,
            width: body.style.width
          },
          scrollY: window.scrollY
        };
        if (root.scrollHeight > root.clientHeight) {
          root.style.scrollbarGutter = "stable";
        }
        root.style.overflow = "hidden";
        body.style.overflow = "hidden";
        if (touch) {
          body.style.position = "fixed";
          body.style.top = `-${saved.scrollY}px`;
          body.style.insetInline = "0";
          body.style.width = "100%";
        }
      }
      lockDepth = lockDepth + 1;
      return () => {
        lockDepth = Math.max(0, lockDepth - 1);
        if (lockDepth === 0 && saved) {
          const {
            scrollY
          } = saved;
          root.style.overflow = saved.overflow;
          root.style.scrollbarGutter = saved.gutter;
          Object.assign(body.style, saved.body);
          saved = null;
          if (touch) {
            window.scrollTo({
              top: scrollY,
              behavior: "instant"
            });
          }
        }
      };
    };
    t1 = [active];
    $[0] = active;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
}

/** True when something between `from` and `stop` is scrolled away from its top. */
function scrolledAbove(from: Element | null, stop: Element) {
  for (let el = from; el && el !== stop.parentElement; el = el.parentElement) {
    if (el.scrollTop > 0) {
      const oy = getComputedStyle(el).overflowY;
      if (oy === "auto" || oy === "scroll") return true;
    }
  }
  return false;
}
const SLOP = 6;
export function useSheetDrag(panelRef, open, onClose) {
  const $ = _c(7);
  const close = useRef(onClose);
  let t0;
  let t1;
  if ($[0] !== onClose) {
    t0 = () => {
      close.current = onClose;
    };
    t1 = [onClose];
    $[0] = onClose;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== open || $[4] !== panelRef) {
    t2 = () => {
      const panel = panelRef.current;
      if (!panel || !open) {
        return;
      }
      panel.style.transform = "";
      panel.style.transition = "";
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      let mode = "idle";
      let fromHead = false;
      let startX = 0;
      let startY = 0;
      let lastY = 0;
      let lastT = 0;
      let velocity = 0;
      let offset = 0;
      let leaving = false;
      const isSheet = () => Math.abs(panel.getBoundingClientRect().bottom - document.documentElement.clientHeight) < 4;
      const onStart = e => {
        mode = "idle";
        if (leaving || e.touches.length !== 1 || !isSheet()) {
          return;
        }
        const t = e.touches[0];
        const target = e.target as Element | null;
        fromHead = !!target?.closest(".v2-panel-head");
        mode = !fromHead && scrolledAbove(target, panel) ? "pass" : "pending";
        startX = t.clientX;
        startY = lastY = t.clientY;
        lastT = e.timeStamp;
        velocity = 0;
        offset = 0;
      };
      const onMove = e_0 => {
        if (mode === "idle" || mode === "pass") {
          return;
        }
        const t_0 = e_0.touches[0];
        const dy = t_0.clientY - startY;
        if (mode === "pending") {
          const dx = t_0.clientX - startX;
          if (Math.abs(dx) > SLOP && Math.abs(dx) > Math.abs(dy)) {
            mode = "pass";
            return;
          }
          if (dy > SLOP || fromHead && dy < -SLOP) {
            mode = "drag";
            panel.style.transition = "none";
          } else {
            if (dy < -SLOP) {
              mode = "pass";
              return;
            } else {
              if (fromHead) {
                e_0.preventDefault();
              }
              return;
            }
          }
        }
        e_0.preventDefault();
        offset = dy > 0 ? dy : -Math.min(18, Math.sqrt(-dy) * 2.4);
        panel.style.transform = `translate3d(0, ${offset}px, 0)`;
        const dt = e_0.timeStamp - lastT;
        if (dt > 0) {
          velocity = (t_0.clientY - lastY) / dt;
        }
        lastY = t_0.clientY;
        lastT = e_0.timeStamp;
      };
      const onEnd = () => {
        if (mode !== "drag") {
          mode = "idle";
          return;
        }
        mode = "idle";
        const swallow = _temp;
        panel.addEventListener("click", swallow, {
          capture: true,
          once: true
        });
        window.setTimeout(() => panel.removeEventListener("click", swallow, {
          capture: true
        }), 350);
        const height = panel.offsetHeight;
        const dismiss = offset > Math.min(140, height * 0.25) || velocity > 0.5 && offset > 24;
        panel.style.transition = reduce ? "none" : `transform ${dismiss ? ".2s" : ".28s"} cubic-bezier(.2, .7, .3, 1)`;
        if (dismiss) {
          leaving = true;
          panel.style.transform = `translate3d(0, ${height + 24}px, 0)`;
          window.setTimeout(() => {
            leaving = false;
            close.current();
          }, reduce ? 0 : 190);
        } else {
          panel.style.transform = "";
        }
      };
      panel.addEventListener("touchstart", onStart, {
        passive: true
      });
      panel.addEventListener("touchmove", onMove, {
        passive: false
      });
      panel.addEventListener("touchend", onEnd);
      panel.addEventListener("touchcancel", onEnd);
      return () => {
        panel.style.transform = "";
        panel.style.transition = "";
        panel.removeEventListener("touchstart", onStart);
        panel.removeEventListener("touchmove", onMove);
        panel.removeEventListener("touchend", onEnd);
        panel.removeEventListener("touchcancel", onEnd);
      };
    };
    t3 = [panelRef, open];
    $[3] = open;
    $[4] = panelRef;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
}
function _temp(ev) {
  ev.stopPropagation();
  ev.preventDefault();
}